/**
 * Retouren v3 (sessie 172): de feiten van één retour.
 *
 * Desktop twee kolommen, telefoon een kolom met "Aangemeld en ontvangen"
 * samen. Verzendkosten omzetten en een afspraak toevoegen gebeuren hier,
 * met een bevestiging of klein formulier in de pagina.
 *
 * Sessie 186: de geldregel en de verzendkostenregel komen uit de backend
 * (``retour_v3_stand.bepaal_geld``); dit scherm leest alleen. Een
 * afgehandelde retour zonder geld in het portaal toont geen bedrag, en
 * "onbekend voor wie" valt weg bij een afgeronde retour.
 */
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { vertaalApiFout } from "@/lib/api";
import { retourenV3Api, type RetourV3Dossier } from "@/lib/retouren-v3-api";

import { euro } from "../../Vandaag/tijd";
import "./dossier-v3.css";

/** "2026-10-16T09:12:00Z" wordt "16 okt"; een al korte tekst blijft staan. */
function korteDatum(waarde: string | null): string | null {
  if (!waarde) return null;
  if (!/^\d{4}-\d{2}-\d{2}/.test(waarde)) return waarde;
  const d = new Date(waarde);
  if (Number.isNaN(d.getTime())) return waarde;
  const dit = new Date().getFullYear() === d.getFullYear();
  return d
    .toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "short",
      year: dit ? undefined : "numeric",
      timeZone: "Europe/Amsterdam",
    })
    .replace(".", "");
}

interface Props {
  dossier: RetourV3Dossier;
  smal: boolean;
  opNieuw: (dossier: RetourV3Dossier) => void;
}

export function DossierFeiten({ dossier, smal, opNieuw }: Props) {
  const feiten = dossier.feiten;
  const kosten = feiten.verzendkosten;
  const extra = kosten;
  const kostenTonen = extra.tonen !== false;
  const heen = extra.heenverzending ?? null;
  const terug = feiten.terug;
  const terugLabel = terug.label || "Terug te betalen";
  const toonBedrag = terug.toon_bedrag !== false;
  const [kostenVragen, setKostenVragen] = useState(false);
  const [afspraakOpen, setAfspraakOpen] = useState(false);
  const [afspraakTekst, setAfspraakTekst] = useState("");
  const [afspraakDatum, setAfspraakDatum] = useState("");
  const [fout, setFout] = useState<string | null>(null);
  const afspraakFormRef = useRef<HTMLFormElement>(null);
  const afspraakVeldRef = useRef<HTMLInputElement>(null);

  // Sessie 186: het formulier opent in beeld. Op de telefoon staat de
  // knoppenbalk (.rv3d-voet) vast onderaan; zonder ruimte schoof het
  // formulier eronder. Daarom eerst de balkhoogte als scroll-margin-bottom,
  // dan in beeld schuiven, dan focus op het eerste veld (zonder dat focus
  // zelf nog eens schuift).
  useEffect(() => {
    if (!afspraakOpen) return;
    const form = afspraakFormRef.current;
    if (!form) return;
    const balk = form.closest(".rv3d")?.querySelector<HTMLElement>(".rv3d-voet");
    const hoogte = balk ? Math.ceil(balk.getBoundingClientRect().height) : 0;
    form.style.scrollMarginBottom = `${hoogte + 12}px`;
    form.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    afspraakVeldRef.current?.focus({ preventScroll: true });
  }, [afspraakOpen]);

  const omzetten = useMutation({
    mutationFn: () => retourenV3Api.verzendkostenNaarOns(dossier.retour_id),
    onSuccess: (nieuw) => {
      setKostenVragen(false);
      setFout(null);
      opNieuw(nieuw);
    },
    onError: (e) =>
      setFout(vertaalApiFout(e, "De verzendkosten zijn niet omgezet.")),
  });

  const heenZetten = useMutation({
    mutationFn: (aan: boolean) => retourenV3Api.heenverzendingTerug(dossier.retour_id, aan),
    onSuccess: (nieuw) => {
      setFout(null);
      opNieuw(nieuw);
    },
    onError: (e) =>
      setFout(vertaalApiFout(e, "De keuze voor de heenverzending is niet opgeslagen.")),
  });

  const afspraak = useMutation({
    mutationFn: () =>
      retourenV3Api.afspraak(dossier.retour_id, {
        tekst: afspraakTekst.trim(),
        termijn_op: afspraakDatum || null,
      }),
    onSuccess: (nieuw) => {
      setAfspraakOpen(false);
      setAfspraakTekst("");
      setAfspraakDatum("");
      setFout(null);
      opNieuw(nieuw);
    },
    onError: (e) =>
      setFout(vertaalApiFout(e, "De afspraak is niet vastgelegd.")),
  });

  const voorTekst =
    kosten.voor === "klant"
      ? "voor de klant"
      : kosten.voor === "voeronline"
        ? "voor ons"
        : "onbekend voor wie";
  const omgezetOp = korteDatum(kosten.omgezet_op);
  const omgezetWie =
    kosten.voor === "voeronline" && (kosten.omgezet_door || omgezetOp)
      ? `, omgezet${kosten.omgezet_door ? ` door ${kosten.omgezet_door}` : ""}${
          omgezetOp ? ` op ${omgezetOp}` : ""
        }`
      : "";

  return (
    <div className="rv3d-feitenblok">
      <dl
        className={smal ? "rv3d-feiten rv3d-feiten-smal" : "rv3d-feiten"}
        data-testid="rv3d-feiten"
      >
        {smal ? (
          <div>
            <dt>Aangemeld en ontvangen</dt>
            <dd>{feiten.aangemeld_en_ontvangen}</dd>
          </div>
        ) : (
          <>
            <div>
              <dt>Aangemeld</dt>
              <dd>{feiten.aangemeld}</dd>
            </div>
            <div>
              <dt>Ontvangen bij Cenfil</dt>
              <dd>{feiten.ontvangen}</dd>
            </div>
          </>
        )}
        <div>
          <dt>Reden</dt>
          <dd>{feiten.reden}</dd>
        </div>
        {kostenTonen ? (
          <div>
            <dt>Verzendkosten</dt>
            <dd data-testid="rv3d-verzendkosten">
              {euro(kosten.bedrag_cents)} {voorTekst}
              {omgezetWie}
              {kosten.omzetbaar && !kostenVragen ? (
                <>
                  {" "}
                  <button
                    type="button"
                    className="rv3d-link"
                    data-testid="rv3d-kosten-omzetten"
                    onClick={() => {
                      setFout(null);
                      setKostenVragen(true);
                    }}
                  >
                    {smal ? "omzetten" : "omzetten naar ons"}
                  </button>
                </>
              ) : null}
              {heen ? (
                <div data-testid="rv3d-heenverzending">
                  {dossier.groep === "afgerond" || heen.kan_wijzigen === false ? (
                    <>
                      Heenverzending {euro(heen.bedrag_cents)}{" "}
                      {heen.aan ? "gaat mee terug" : "gaat niet mee terug"}
                      {heen.reden ? "." : null}
                      {heen.reden ? (
                        <span data-testid="rv3d-heenverzending-reden">
                          {" "}
                          {heen.reden}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <label>
                      <input
                        type="checkbox"
                        data-testid="rv3d-heenverzending-keuze"
                        checked={heen.aan}
                        disabled={heenZetten.isPending}
                        onChange={(e) => heenZetten.mutate(e.target.checked)}
                      />{" "}
                      Ook de heenverzending terug ({euro(heen.bedrag_cents)})
                    </label>
                  )}
                </div>
              ) : null}
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Afspraak met de klant</dt>
          <dd data-testid="rv3d-afspraken">
            {feiten.afspraken.length === 0 ? (
              "Geen"
            ) : (
              feiten.afspraken.map((a) => (
                <div key={a.id} className="rv3d-afspraak">
                  {a.tekst}
                  {a.termijn ? `, uiterlijk ${a.termijn}` : ""}
                </div>
              ))
            )}
            {!afspraakOpen ? (
              <>
                {" "}
                <button
                  type="button"
                  className="rv3d-link"
                  data-testid="rv3d-afspraak-toevoegen"
                  onClick={() => {
                    setFout(null);
                    setAfspraakOpen(true);
                  }}
                >
                  toevoegen
                </button>
              </>
            ) : null}
          </dd>
        </div>
        <div>
          <dt>{terugLabel}</dt>
          {toonBedrag ? (
            <dd className="rv3d-terug-bedrag" data-testid="rv3d-terug">
              {euro(terug.totaal_cents)}
              {terug.zin ? `, ${terug.zin}` : null}
              {terug.rekensom ? (
                <>
                  {" "}
                  <span className="rv3d-rekensom">{terug.rekensom}</span>
                </>
              ) : null}
            </dd>
          ) : (
            <dd data-testid="rv3d-terug">{terug.zin || "Geen bedrag"}</dd>
          )}
        </div>
      </dl>

      {kostenVragen ? (
        <div className="rv3d-paneel" data-testid="rv3d-kosten-bevestig" style={{ marginTop: 14 }}>
          <p>
            Verzendkosten {euro(kosten.bedrag_cents)} voor ons? Dan gaan ze niet
            meer van de terugbetaling af.
          </p>
          <div className="rv3d-paneel-knoppen">
            <button
              type="button"
              className="rv3d-knop rv3d-knop-klein"
              data-testid="rv3d-kosten-ja"
              disabled={omzetten.isPending}
              onClick={() => omzetten.mutate()}
            >
              {omzetten.isPending ? "Bezig…" : "Ja, voor ons"}
            </button>
            <button
              type="button"
              className="rv3d-knop rv3d-knop-klein"
              onClick={() => setKostenVragen(false)}
            >
              Nog niet
            </button>
          </div>
        </div>
      ) : null}

      {afspraakOpen ? (
        <form
          ref={afspraakFormRef}
          className="rv3d-paneel rv3d-afspraak-form"
          data-testid="rv3d-afspraak-form"
          style={{ marginTop: 14 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (afspraakTekst.trim()) afspraak.mutate();
          }}
        >
          <label className="rv3d-veldlabel">
            Afspraak met de klant
            <input
              ref={afspraakVeldRef}
              type="text"
              className="rv3d-veld"
              data-testid="rv3d-afspraak-tekst"
              value={afspraakTekst}
              onChange={(e) => setAfspraakTekst(e.target.value)}
            />
          </label>
          <label className="rv3d-veldlabel">
            Uiterlijk (mag leeg)
            <input
              type="date"
              className="rv3d-veld"
              data-testid="rv3d-afspraak-datum"
              value={afspraakDatum}
              onChange={(e) => setAfspraakDatum(e.target.value)}
            />
          </label>
          <div className="rv3d-paneel-knoppen">
            <button
              type="submit"
              className="rv3d-knop rv3d-knop-klein"
              data-testid="rv3d-afspraak-opslaan"
              disabled={!afspraakTekst.trim() || afspraak.isPending}
            >
              {afspraak.isPending ? "Bezig…" : "Vastleggen"}
            </button>
            <button
              type="button"
              className="rv3d-knop rv3d-knop-klein"
              onClick={() => setAfspraakOpen(false)}
            >
              Annuleren
            </button>
          </div>
        </form>
      ) : null}

      {fout ? (
        <div className="rv3d-fout" role="alert" style={{ marginTop: 10 }}>
          {fout}
        </div>
      ) : null}
    </div>
  );
}
